import { useMemo, useState } from 'react';
import type { Item } from '../../../types/prioritization';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import type { SessionParticipant } from '../../../types/prioritization/session';
import { resolveRunMode } from '../../../types/prioritization/session';
import { C } from '../theme/participantTokens';
import { useSystemUsers, type SystemUser } from '../../../hooks/useSystemUsers';
import {
  isRankingSession,
  isSessionCompleted,
  isSessionLive,
  isValueSession,
  toolDisplayName,
} from '../utils/sessionHelpers';
import { LayerBack, LayerBar } from './layout/ParticipantLayout';
import { resolveSessionScoringCriteria } from '../../../lib/sessionScoringCriteria';
import { uniqueLines } from '@/lib/uniqueLines';

interface SessionBriefProps {
  session: PrioritizationSession;
  items: Item[];
  participants: SessionParticipant[];
  userId: string;
  onStart: () => void;
  onBack: () => void;
  /** S-3: names are hidden in this session — the brief gives a count. */
  hideNames?: boolean;
}

export function SessionBrief({
  session,
  items,
  participants,
  userId,
  onStart,
  onBack,
  hideNames = false,
}: SessionBriefProps) {

  /**
   * Criterion NAMES (defect, 2026-09-03).
   *
   * `criteriaConfig` carries ids and weights only, and this brief used to
   * render the id with its separators swapped for spaces as if that were a
   * label. For an attribute-keyed criterion ("impact") that reads fine, which
   * is why it survived; for a session-authored one it put "crit 99d04d" in
   * front of a participant as the name of the thing they were being asked to
   * score.
   *
   * The scoring tools already solved this — `resolveSessionScoringCriteria`
   * falls back through criteria_config → v3Config (where authored criteria keep
   * their human names) → the tool's floor. The brief simply never used it, so
   * the same session read one way in the tool and another way here. Use the one
   * implementation rather than keeping a second, worse copy.
   */
  const resolvedCriteria = useMemo(
    () => resolveSessionScoringCriteria(session, []).criteria,
    [session],
  );

  const isLive = isSessionLive(session);
  const isDone = isSessionCompleted(session);
  const runMode = resolveRunMode(session.modeConfig);
  // Facilitator-entry: the person arriving created and owns this session.
  // DRAFT state just means it hasn't been formally started — not that it's not ready.
  const isFacilitatorEntry = (session.modeConfig.entryMode ?? 'facilitator') === 'facilitator';
  const isStillConfiguring = session.state === 'DRAFT' && !isFacilitatorEntry;

  // Resolve participant display names from workspace users
  const { users: wsUsers } = useSystemUsers({ workspaceId: session.workspaceId ?? undefined });
  const nameMap = useMemo<Record<string, string>>(() => {
    const m: Record<string, string> = {};
    wsUsers.forEach((u: SystemUser) => { if (u.id) m[u.id] = u.name || u.email || u.id; });
    return m;
  }, [wsUsers]);

  // V3 sessions store items in mode_config.v3Config — fall back to that count so the
  // CTA is never hidden just because item_ids is empty.
  const v3ConfigItemCount = (((session.modeConfig as Record<string, unknown>)?.v3Config as { items?: unknown[] })?.items ?? []).length;
  // An Isolated session ranks options typed for it (the Session app's default),
  // which are not workspace items — count those, or the brief says "0 items".
  const isolatedOptionCount =
    session.sessionContextMode === 'isolated' ? uniqueLines(session.isolatedItems ?? []).length : 0;
  const hasItems = items.length > 0 || v3ConfigItemCount > 0 || isolatedOptionCount > 0;

  // Use V3 config item count when session items aren't in the standard store yet
  const displayItemCount = items.length > 0 ? items.length : v3ConfigItemCount || isolatedOptionCount;

  const estimatedMinutes = useMemo(() => {
    const n = displayItemCount;
    if (isRankingSession(session)) return Math.max(5, Math.round((n * (n - 1)) / 2 * 0.25));
    return session.modeConfig?.expectedDuration ?? 20;
  }, [displayItemCount, session]);

  const purposeCopy = useMemo(() => {
    if (isValueSession(session)) {
      return `Enter values for ${displayItemCount} items on each criterion. Only fill in what you know — gaps will be filled by others.`;
    }
    if (isRankingSession(session)) {
      return `Compare ${displayItemCount} items using ${toolDisplayName(session)}. Your input${participants.length > 1 ? `, combined with ${participants.length - 1} other participant${participants.length > 2 ? 's' : ''},` : ''} produces the session result.`;
    }
    return 'Complete your contribution for this configuration step.';
  }, [session, displayItemCount, participants.length]);

  const ctaLabel = isDone
    ? 'View Results'
    : isRankingSession(session) && runMode !== 'open'
      ? 'Join Live Session'
      : isRankingSession(session)
        ? 'Start Ranking Session'
        : isValueSession(session)
          ? 'Enter Values'
          : 'Begin Contribution';

  return (
    <>
      <LayerBar>
        <LayerBack label="Sessions" onClick={onBack} />
        <div style={{ padding: '0 16px', fontFamily: 'Spectral, Georgia, serif', fontSize: '0.88rem', color: C.textSec, borderRight: `1px solid ${C.rule}` }}>
          {session.name}
        </div>
        {isLive && !isDone && (
          <div style={{ padding: '0 14px', fontFamily: 'JetBrains Mono', fontSize: '0.52rem', color: C.coral, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span className="pp-live-dot" style={{ width: 5, height: 5, borderRadius: '50%', background: C.coral, flexShrink: 0 }} />
            Live Now
          </div>
        )}
      </LayerBar>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '28px 32px 48px', maxWidth: 860 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderLeft: `3px solid ${C.mustard}`, borderRadius: 4, padding: '20px 22px', marginBottom: 14 }}>
            <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: 'rgba(212,160,23,0.7)', marginBottom: 6 }}>
              {toolDisplayName(session)}
            </div>
            <div style={{ fontFamily: 'Spectral, Georgia, serif', fontSize: '1.6rem', fontWeight: 600, color: C.textPri, marginBottom: 8 }}>
              {session.name}
            </div>
            <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.85rem', fontWeight: 300, color: C.textTer, lineHeight: 1.6, margin: 0 }}>
              {purposeCopy}
            </p>
            <div style={{ display: 'flex', gap: 28, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${C.rule}`, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1.15rem', fontWeight: 700, color: C.mustard }}>{displayItemCount}</div>
                <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.72rem', color: C.textTer, marginTop: 2 }}>Items</div>
              </div>
              <div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1.15rem', fontWeight: 700, color: C.mustard }}>~{estimatedMinutes}</div>
                <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.72rem', color: C.textTer, marginTop: 2 }}>Minutes est.</div>
              </div>
              <div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: '1.15rem', fontWeight: 700, color: C.mustard }}>{participants.length}</div>
                <div style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.72rem', color: C.textTer, marginTop: 2 }}>Participants</div>
              </div>
            </div>
          </div>

          {resolvedCriteria.length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '16px 18px', marginBottom: 10 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.16em', color: C.textTer, textTransform: 'uppercase', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.rule}` }}>
                Criteria
              </div>
              {resolvedCriteria.map(c => {
                const pct = c.weight;
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '5px 0' }}>
                    <span style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.8rem', color: C.textSec, width: 155, flexShrink: 0 }} title={c.description || undefined}>
                      {c.name}
                    </span>
                    <div style={{ flex: 1, height: 3, background: C.ruleBright, borderRadius: 999, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: C.mustard, opacity: 0.7 }} />
                    </div>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.6rem', color: C.mustard, width: 30, textAlign: 'right' }}>{pct}%</span>
                  </div>
                );
              })}
            </div>
          )}

          {participants.length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '16px 18px', marginBottom: 10 }}>
              <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.16em', color: C.textTer, textTransform: 'uppercase', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.rule}` }}>
                Participants
              </div>
              {hideNames ? (
                <div style={{ fontSize: '0.8rem', color: C.textSec }} data-testid="brief-roster-count">
                  {participants.length} {participants.length === 1 ? 'person takes' : 'people take'} part. Names are hidden in this session.
                </div>
              ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {participants.map(p => {
                  const isMe = p.userId === userId;
                  const resolved = nameMap[p.userId];
                  const storedName = (p as SessionParticipant & { name?: string }).name;
                  const isUuid = (s: string) => /^[0-9a-f-]{8,}$/i.test(s);
                  const displayName = resolved
                    ? resolved
                    : (storedName && !isUuid(storedName))
                      ? storedName
                      : p.userId.slice(0, 8);
                  return (
                    <span
                      key={p.id}
                      style={{
                        fontFamily: 'JetBrains Mono', fontSize: '0.55rem', padding: '3px 8px',
                        border: `1px solid ${isMe ? 'rgba(212,160,23,0.5)' : C.ruleBright}`,
                        borderRadius: 2, color: isMe ? C.mustard : C.textTer,
                        background: isMe ? 'rgba(212,160,23,0.08)' : 'none', fontWeight: isMe ? 700 : 400,
                      }}
                    >
                      {displayName}{isMe ? ' · You' : ''}
                    </span>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {isStillConfiguring && (
            <div style={{ background: C.surface, border: `1px solid ${C.rule}`, borderRadius: 4, padding: '16px 18px', marginBottom: 10, color: C.textTer, fontSize: '0.85rem' }}>
              The facilitator is still configuring this session. Check back soon.
            </div>
          )}

          {/* Facilitators can always proceed — don't block on item count */}
          {(hasItems || isFacilitatorEntry || !isRankingSession(session)) && !isStillConfiguring && (
            <button
              type="button"
              onClick={onStart}
              style={{
                width: '100%', height: 42, background: C.activeTint, border: `1px solid ${C.slate}`,
                borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontSize: '0.7rem',
                letterSpacing: '0.15em', textTransform: 'uppercase', color: C.textSec, marginTop: 10,
              }}
            >
              {ctaLabel} →
            </button>
          )}

          {/* Facilitator invite link — lets them share the room URL with participants */}
          {isFacilitatorEntry && (
            <InviteLink sessionId={session.id} />
          )}
        </div>
      </div>
    </>
  );
}

function InviteLink({ sessionId }: { sessionId: string }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/prioritization/session/${sessionId}`;

  function copy() {
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.rule}`, borderRadius: 3 }}>
      <div style={{ fontFamily: 'JetBrains Mono', fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: C.textTer, marginBottom: 8 }}>
        Participant join link
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, fontFamily: 'JetBrains Mono', fontSize: '0.6rem', color: C.textSec, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.7 }}>
          {link}
        </div>
        <button
          type="button"
          onClick={copy}
          style={{
            flexShrink: 0, height: 26, padding: '0 10px',
            background: copied ? 'rgba(83,103,91,0.3)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${copied ? 'rgba(83,103,91,0.6)' : C.rule}`,
            borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono',
            fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            color: copied ? '#6CA87A' : C.textTer, transition: 'all 0.15s',
          }}
        >
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <p style={{ fontFamily: 'degular-variable, -apple-system, BlinkMacSystemFont, system-ui, sans-serif', fontSize: '0.68rem', color: C.textDim, marginTop: 6 }}>
        Share this with participants so they can join directly.
      </p>
    </div>
  );
}
